import { computed, unref } from 'vue';
import {
  buildRealEstateCapabilityContext,
  getPermissionTokensForAccount,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';

const DISABLED_REASON_KEYS = Object.freeze({
  capability_denied: 'REAL_ESTATE_NAVIGATION.DISABLED_REASONS.CAPABILITY',
  partial: 'REAL_ESTATE_NAVIGATION.DISABLED_REASONS.PARTIAL',
  stale: 'REAL_ESTATE_NAVIGATION.DISABLED_REASONS.STALE',
  permission_denied: 'REAL_ESTATE_NAVIGATION.DISABLED_REASONS.PERMISSION',
});

const GUIDE_URL = '/lais/brand-voice';
const HELP_URL = '/lais/suporte';

const item = ({
  key,
  labelKey,
  icon,
  routeName,
  params,
  query,
  href,
  activeOn = [],
  permissions = [],
  surface,
  action = 'view',
  badge,
}) => ({
  key,
  labelKey,
  icon,
  routeName,
  params,
  query,
  href,
  activeOn,
  permissions,
  surface,
  action,
  badge,
});

export const REAL_ESTATE_NAVIGATION_SECTIONS = Object.freeze([
  {
    key: 'core',
    titleKey: '',
    items: Object.freeze([
      item({
        key: 'inicio',
        labelKey: 'SIDEBAR.HOME',
        icon: 'i-lucide-home',
        routeName: 'inicio_index',
        activeOn: ['inicio_index'],
        permissions: ['administrator', 'agent', 'custom_role'],
      }),
      item({
        key: 'conversas',
        labelKey: 'SIDEBAR.CONVERSATIONS',
        icon: 'i-lucide-message-circle',
        routeName: 'home',
        activeOn: [
          'home',
          'inbox_conversation',
          'conversation_mentions',
          'conversation_through_mentions',
          'conversation_unattended',
          'conversation_through_unattended',
          'folder_conversations',
          'conversations_through_folders',
          'team_conversations',
          'conversations_through_team',
          'inbox_dashboard',
          'conversation_through_inbox',
          'label_conversations',
          'conversations_through_label',
          'conversation_participating',
          'conversation_through_participating',
        ],
        permissions: [
          'administrator',
          'agent',
          'custom_role',
          'conversation_manage',
          'conversation_unassigned_manage',
          'conversation_participating_manage',
        ],
      }),
    ]),
  },
  {
    key: 'commercial',
    titleKey: 'REAL_ESTATE_NAVIGATION.SECTIONS.COMMERCIAL',
    items: Object.freeze([
      item({
        key: 'clientes',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CLIENTES',
        icon: 'i-lucide-briefcase',
        routeName: 'lais_leads_index',
        activeOn: [
          'lais_clients_grouped',
          'lais_leads_index',
          'lais_lead_historico',
          'lais_leads_pipeline',
          'lais_lead_referrals',
          'lais_crm_tasks',
          'lais_property_demands_console',
          'lais_blocked_contacts_console',
          'lais_kyc_risk_review_queue',
          'lais_inquiries_index',
        ],
        permissions: ['administrator', 'agent', 'leads_manage'],
        surface: REAL_ESTATE_SURFACES.LEADS_INDEX,
      }),
      item({
        key: 'favoritos',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.FAVORITOS',
        icon: 'i-lucide-heart',
        routeName: 'favoritos_index',
        activeOn: ['favoritos_index', 'favoritos_show'],
        permissions: ['administrator', 'agent'],
      }),
      item({
        key: 'mercado',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.MERCADO',
        icon: 'i-lucide-store',
        routeName: 'pending_market_updates_index',
        activeOn: [
          'mercado_index',
          'pending_market_updates_index',
          'mudancas_index',
        ],
        permissions: ['administrator', 'agent', 'captador', 'captacao_manage'],
        surface: REAL_ESTATE_SURFACES.PENDING_MARKET_INDEX,
      }),
      item({
        key: 'visitas',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.VISITAS',
        icon: 'i-lucide-calendar-check',
        routeName: 'visitas_index',
        activeOn: [
          'visitas_index',
          'visita_show',
          'visita_new',
          'visita_edit',
          'visita_finish',
          'visita_review',
          'visita_success',
          'visitor_info',
          'visita_deep_link',
          'visita_deep_link_finish',
          'visita_deep_link_cancel',
          'visita_deep_link_reschedule',
          'open_house_index',
          'open_house_show',
        ],
        badge: 'NOVO',
        permissions: [
          'administrator',
          'agent',
          'visitas_manage',
          'chaves_manage',
        ],
        surface: REAL_ESTATE_SURFACES.VISITAS_INDEX,
      }),
      item({
        key: 'relatorio',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.RELATORIO',
        icon: 'i-lucide-clipboard-list',
        routeName: 'relatorio_index',
        activeOn: [
          'relatorio_index',
          'desempenho_equipe_index',
          'metas_index',
          'operational_history_index',
          'account_overview_reports',
          'conversation_reports',
          'agent_reports',
          'inbox_reports',
          'label_reports',
          'team_reports',
          'agent_reports_index',
          'agent_reports_show',
          'inbox_reports_index',
          'inbox_reports_show',
          'team_reports_index',
          'team_reports_show',
          'label_reports_index',
          'label_reports_show',
          'csat_reports',
          'sla_reports',
          'bot_reports',
        ],
        permissions: ['administrator', 'agent', 'report_manage'],
        surface: REAL_ESTATE_SURFACES.REAL_ESTATE_REPORTS_INDEX,
      }),
      item({
        key: 'propostas',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.PROPOSTAS',
        icon: 'i-lucide-file-text',
        routeName: 'lais_tenants_rent_flows',
        activeOn: [
          'lais_tenants_rent_flows',
          'lais_tenants_rent_flows_status',
          'lais_tenants_rent_flows_status_phase',
        ],
        permissions: ['administrator', 'agent'],
        surface: REAL_ESTATE_SURFACES.PROPERTY_OFFERS_INDEX,
      }),
      item({
        key: 'meu-aluguel',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.MEU_ALUGUEL',
        icon: 'i-lucide-receipt',
        routeName: 'lais_meu_aluguel_index',
        activeOn: [
          'lais_meu_aluguel_index',
          'lais_meu_aluguel_detail',
          'lais_meu_aluguel_detail_section',
        ],
        permissions: ['administrator', 'agent'],
        surface: REAL_ESTATE_SURFACES.RENT_FLOWS_INDEX,
      }),
      item({
        key: 'qpreco',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.QPRECO',
        icon: 'i-lucide-line-chart',
        routeName: 'lais_area_qpreco_index',
        activeOn: [
          'lais_area_qpreco_index',
          'lais_area_qpreco_show',
          'lais_area_qpreco_section',
        ],
        permissions: ['administrator', 'agent'],
        surface: REAL_ESTATE_SURFACES.QPRECO_INDEX,
      }),
    ]),
  },
  {
    key: 'propertyShelf',
    titleKey: 'REAL_ESTATE_NAVIGATION.SECTIONS.PROPERTY_SHELF',
    items: Object.freeze([
      item({
        key: 'criar-imovel',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CRIAR_IMOVEL',
        icon: 'i-lucide-plus-circle',
        routeName: 'lais_criar_imovel_index',
        activeOn: ['lais_criar_imovel_index', 'imovel_new'],
        permissions: ['administrator', 'agent', 'imoveis_manage'],
        surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
        action: 'create',
      }),
      item({
        key: 'imoveis',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.IMOVEIS',
        icon: 'i-lucide-building',
        routeName: 'imoveis_index',
        query: { catalog: 'empreendimentos' },
        activeOn: [
          'imoveis_index',
          'imovel_show',
          'imovel_show_section',
          'imovel_catalog_show',
          'imovel_catalog_section',
          'imovel_edit',
        ],
        permissions: ['administrator', 'agent', 'imoveis_manage'],
        surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
      }),
      item({
        key: 'importacoes',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.IMPORTACOES',
        icon: 'i-lucide-upload-cloud',
        routeName: 'imoveis_import_operations',
        activeOn: ['imoveis_import_operations'],
        permissions: ['administrator', 'agent', 'imoveis_manage'],
        surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
        action: 'import_console',
      }),
      item({
        key: 'portais',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.PORTAIS',
        icon: 'i-lucide-radio-tower',
        routeName: 'imoveis_portal_sync_operations',
        activeOn: [
          'imoveis_portal_sync_operations',
          'imoveis_portal_integrations_console',
        ],
        permissions: ['administrator', 'agent', 'imoveis_manage'],
        surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
        action: 'portal_sync_operations',
      }),
      item({
        key: 'canalpro-credenciais',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CANALPRO_CREDENCIAIS',
        icon: 'i-lucide-key-round',
        routeName: 'canalpro_credentials_index',
        activeOn: ['canalpro_credentials_index'],
        permissions: ['administrator'],
      }),
      item({
        key: 'canalpro-contas',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CANALPRO_CONTAS',
        icon: 'i-lucide-building-2',
        routeName: 'canalpro_accounts_index',
        activeOn: ['canalpro_accounts_index'],
        permissions: ['administrator'],
      }),
      item({
        key: 'canalpro-anuncios',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CANALPRO_ANUNCIOS',
        icon: 'i-lucide-megaphone',
        routeName: 'canalpro_listings_index',
        activeOn: [
          'canalpro_listings_index',
          'canalpro_listing_new',
          'canalpro_listing_edit',
        ],
        permissions: ['administrator', 'agent'],
      }),
      item({
        key: 'data-registry',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.DATA_REGISTRY',
        icon: 'i-lucide-database',
        routeName: 'lais_data_registry_index',
        activeOn: ['lais_data_registry_index'],
        permissions: [
          'administrator',
          'data_registry_view',
          'data_registry_manage',
          'data_registry_sensitive_view',
        ],
      }),
      item({
        key: 'stories',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.STORIES',
        icon: 'i-lucide-gallery-horizontal-end',
        routeName: 'lais_stories_index',
        activeOn: ['lais_stories_index'],
        permissions: ['administrator', 'agent', 'vitrine_manage'],
      }),
      item({
        key: 'duplicidades',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.DUPLICIDADES',
        icon: 'i-lucide-git-compare-arrows',
        routeName: 'imoveis_duplicate_candidates',
        activeOn: ['imoveis_duplicate_candidates'],
        permissions: ['administrator'],
        surface: REAL_ESTATE_SURFACES.IMOVEIS_INDEX,
        action: 'duplicate_candidates',
      }),
      item({
        key: 'prateleira',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.PRATELEIRA',
        icon: 'i-lucide-panels-top-left',
        routeName: 'lais_prateleira_captacao',
        activeOn: [
          'lais_prateleira_index',
          'lais_prateleira_atualizacao',
          'lais_prateleira_captacao',
          'lais_prateleira_importacao',
          'lais_prateleira_publicacao',
          'lais_prateleira_integridade',
          'lais_atualizacao_index',
          'lais_captacao_index',
        ],
        permissions: ['administrator', 'agent', 'captador', 'captacao_manage'],
      }),
    ]),
  },
  {
    key: 'settings',
    titleKey: 'REAL_ESTATE_NAVIGATION.SECTIONS.SETTINGS',
    items: Object.freeze([
      item({
        key: 'area-da-conta',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CONTA',
        icon: 'i-lucide-user-round-cog',
        routeName: 'conta_index',
        activeOn: [
          'conta_index',
          'deletar_conta_index',
          'deletar_conta_sucesso_index',
          'meus_negocios_index',
          'minha_lais_index',
          'minha_lais_whatsapp_catalog_index',
          'minha_lais_whatsapp_catalog_new',
          'minha_lais_whatsapp_catalog_show',
          'minha_lais_whatsapp_catalog_edit',
          'general_settings_index',
          'administracao_index',
          'administracao_qualidade_fraude_index',
          'administracao_parceiros_index',
          'agent_list',
          'settings_teams_list',
          'settings_teams_new',
          'settings_teams_finish',
          'settings_teams_add_agents',
          'settings_teams_edit',
          'settings_teams_edit_members',
          'settings_teams_edit_finish',
          'notifications_index',
          'security_settings_index',
        ],
        permissions: ['administrator', 'agent', 'custom_role', 'agency_manage'],
      }),
      item({
        key: 'vendas',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.VENDAS',
        icon: 'i-lucide-building-2',
        routeName: 'operacao_imobiliaria_vendas',
        activeOn: [
          'operacao_imobiliaria_vendas',
          'operacao_imobiliaria_vendas_detalhes',
        ],
        permissions: [
          'administrator',
          'agent',
          'transactions_view',
          'transactions_manage',
          'post_sale_view',
          'post_sale_manage',
        ],
        surface: REAL_ESTATE_SURFACES.SALES_INDEX,
      }),
      item({
        key: 'pos-venda',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.POST_SALE',
        icon: 'i-lucide-handshake',
        routeName: 'operacao_imobiliaria_pos_venda',
        activeOn: ['operacao_imobiliaria_pos_venda'],
        permissions: [
          'administrator',
          'agent',
          'post_sale_view',
          'post_sale_manage',
          'post_sale_assign',
          'post_sale_status_change',
        ],
        surface: REAL_ESTATE_SURFACES.POST_SALE_INDEX,
      }),
      item({
        key: 'nucleo',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.NUCLEO',
        icon: 'i-lucide-stamp',
        routeName: 'operacao_imobiliaria_nucleo',
        activeOn: ['operacao_imobiliaria_nucleo'],
        permissions: [
          'administrator',
          'agent',
          'post_sale_view',
          'post_sale_manage',
          'post_sale_assign',
          'post_sale_status_change',
          'partners_manage',
        ],
        surface: REAL_ESTATE_SURFACES.POST_SALE_INDEX,
      }),
      item({
        key: 'financeiro',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.FINANCEIRO',
        icon: 'i-lucide-credit-card',
        routeName: 'operacao_imobiliaria_financeiro',
        activeOn: ['operacao_imobiliaria_financeiro'],
        permissions: [
          'administrator',
          'agent',
          'financeiro_view',
          'report_manage',
        ],
        surface: REAL_ESTATE_SURFACES.FINANCEIRO_INDEX,
      }),
      item({
        key: 'corretor',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.CORRETOR',
        icon: 'i-lucide-badge-dollar-sign',
        routeName: 'operacao_imobiliaria_corretor',
        activeOn: [
          'operacao_imobiliaria_corretor',
          'operacao_imobiliaria_corretor_investimentos',
        ],
        permissions: [
          'administrator',
          'agent',
          'financeiro_view',
          'report_manage',
        ],
        surface: REAL_ESTATE_SURFACES.FINANCEIRO_INDEX,
      }),
      item({
        key: 'planos',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.PLANOS',
        icon: 'i-lucide-sparkles',
        routeName: 'planos_index',
        activeOn: ['planos_index'],
        permissions: ['administrator'],
      }),
      item({
        key: 'agentes-ia',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.AGENTES_IA',
        icon: 'i-lucide-bot-message-square',
        routeName: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_experience_index' },
        activeOn: [
          'captain_assistants_create_index',
          'captain_assistants_overview_index',
          'captain_assistants_experience_index',
          'captain_assistants_responses_index',
          'captain_assistants_responses_pending',
          'captain_assistants_documents_index',
          'captain_assistants_scenarios_index',
          'captain_assistants_playground_index',
          'captain_assistants_inboxes_index',
          'captain_account_tools_index',
          'captain_tools_index',
          'captain_assistants_settings_index',
          'captain_assistants_guidelines_index',
          'captain_assistants_guardrails_index',
        ],
        permissions: ['administrator', 'agent'],
      }),
    ]),
  },
  {
    key: 'help',
    titleKey: 'REAL_ESTATE_NAVIGATION.SECTIONS.HELP',
    items: Object.freeze([
      item({
        key: 'tickets',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.TICKETS',
        icon: 'i-lucide-ticket',
        routeName: 'support_tickets_portal_index',
        activeOn: ['support_tickets_portal_index'],
        permissions: ['administrator', 'agent', 'custom_role'],
        surface: REAL_ESTATE_SURFACES.SUPPORT_TICKETS_INDEX,
      }),
      item({
        key: 'guia-lais',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.GUIA_LAIS',
        icon: 'i-lucide-graduation-cap',
        href: GUIDE_URL,
      }),
      item({
        key: 'ajuda',
        labelKey: 'REAL_ESTATE_NAVIGATION.ITEMS.AJUDA',
        icon: 'i-lucide-circle-help',
        href: HELP_URL,
      }),
    ]),
  },
]);

const toArray = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const translate = (t, key) => {
  if (!key) return '';
  // Navigation descriptors store audited literal i18n keys; the helper keeps
  // the catalogue data-driven while every key remains defined in locale files.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return typeof t === 'function' ? t(key) : key;
};

const routeTargetFor = ({ routeFor, routeName, params, query }) => {
  if (!routeName) return undefined;
  if (typeof routeFor === 'function') return routeFor(routeName, params, query);
  return { name: routeName, params, query };
};

const buildContextCache = ({ account, permissions, capabilities, meta }) => {
  const cache = new Map();
  return surface => {
    if (!surface) return null;
    if (!cache.has(surface)) {
      cache.set(
        surface,
        buildRealEstateCapabilityContext({
          surface,
          account,
          permissions,
          capabilities,
          meta,
        })
      );
    }
    return cache.get(surface);
  };
};

const resolveCapability = (navItem, contextFor) => {
  if (!navItem.surface) return null;
  const context = contextFor(navItem.surface);
  return context?.actions?.[navItem.action || 'view'] || null;
};

const isVisibleByPermission = (navItem, permissionTokens) => {
  const requiredPermissions = toArray(navItem.permissions);
  return (
    requiredPermissions.length === 0 ||
    hasPermissions(requiredPermissions, permissionTokens)
  );
};

const materializeItem = ({
  navItem,
  t,
  routeFor,
  permissionTokens,
  contextFor,
}) => {
  if (!isVisibleByPermission(navItem, permissionTokens)) return null;

  const capability = resolveCapability(navItem, contextFor);
  if (capability && !capability.visible) return null;

  const disabled = Boolean(capability?.disabled);
  const disabledReason = capability?.deniedBy || '';
  const disabledReasonKey = DISABLED_REASON_KEYS[disabledReason] || '';
  const disabledReasonLabel = disabledReasonKey
    ? translate(t, disabledReasonKey)
    : '';
  const label = translate(t, navItem.labelKey);

  return {
    ...navItem,
    label,
    to: routeTargetFor({
      routeFor,
      routeName: navItem.routeName,
      params: navItem.params,
      query: navItem.query,
    }),
    disabled,
    disabledReason,
    disabledReasonLabel,
    title: disabledReasonLabel || label,
    capability,
  };
};

export const buildRealEstateNavigationSections = ({
  t,
  routeFor,
  account = {},
  permissions,
  capabilities,
  meta = {},
} = {}) => {
  const permissionTokens =
    permissions || getPermissionTokensForAccount(account);
  const contextFor = buildContextCache({
    account,
    permissions: permissionTokens,
    capabilities,
    meta,
  });

  return REAL_ESTATE_NAVIGATION_SECTIONS.map(section => ({
    key: section.key,
    title: translate(t, section.titleKey),
    items: section.items
      .map(navItem =>
        materializeItem({
          navItem,
          t,
          routeFor,
          permissionTokens,
          contextFor,
        })
      )
      .filter(Boolean),
  })).filter(section => section.items.length > 0);
};

export const useRealEstateNavigation = options =>
  computed(() => {
    const resolvedOptions = Object.fromEntries(
      Object.entries(options || {}).map(([key, value]) => [key, unref(value)])
    );
    return buildRealEstateNavigationSections(resolvedOptions);
  });

export const REQUIRED_REAL_ESTATE_ROUTE_NAMES = Object.freeze([
  ...new Set(
    REAL_ESTATE_NAVIGATION_SECTIONS.flatMap(section =>
      section.items.map(navItem => navItem.routeName).filter(Boolean)
    )
  ),
]);
